'use client'

import {useCallback, useEffect, useRef, useState} from 'react'
import {Minus, Plus} from 'lucide-react'

import Modal from '@/components/Modal/Modal'
import {Button} from '@/components/UiElements'
import {closeNotif, startNotif} from '@/components/DynamicForm/util/notifier'
import getHistoryLineItem from '@/services/OrderService/getHistoryLineItem'
import increaseQuantity from '@/services/OrderService/increaseQuantity'
import type {historyLineItem, SingleOrderDetail} from '@/types/order'
import {getErrorMessage} from '@/utils/errorMessages'
import HistoryTimeline from './HistoryTimeline'
import OrderJobsPanel from './OrderJobsPanel'

interface LineItemProgressHistoryDialogProps {
    isOpen: boolean
    orderId: string
    lineItem: SingleOrderDetail | null
    onClose: () => void
    onQuantityUpdated: (lineItemId: string, quantity: number) => void
}

const LineItemProgressHistoryDialog = ({
    isOpen,
    orderId,
    lineItem,
    onClose,
    onQuantityUpdated,
}: LineItemProgressHistoryDialogProps) => {
    const [currentQuantity, setCurrentQuantity] = useState(0)
    const [desiredQuantity, setDesiredQuantity] = useState(0)
    const [isSaving, setIsSaving] = useState(false)
    const [history, setHistory] = useState<historyLineItem[]>([])
    const [isHistoryLoading, setIsHistoryLoading] = useState(false)
    const [historyError, setHistoryError] = useState('')
    const historyRequestId = useRef(0)

    const loadHistory = useCallback(async () => {
        if (!lineItem) return

        const requestId = ++historyRequestId.current
        setIsHistoryLoading(true)
        setHistoryError('')

        try {
            const result = await getHistoryLineItem(orderId, lineItem.id)
            if (requestId !== historyRequestId.current) return
            setHistory(result)
        } catch (error) {
            if (requestId !== historyRequestId.current) return
            setHistoryError(getErrorMessage(error, 'Unable to load line item history.'))
        }

        setIsHistoryLoading(false)
    }, [lineItem?.id, orderId])

    useEffect(() => {
        if (!isOpen || !lineItem) return

        setCurrentQuantity(lineItem.quantity)
        setDesiredQuantity(lineItem.quantity)
        setIsSaving(false)
    }, [isOpen, lineItem?.id, lineItem?.quantity])

    useEffect(() => {
        setHistory([])
        setHistoryError('')
    }, [lineItem?.id])

    useEffect(() => {
        if (isOpen && lineItem) {
            void loadHistory()
            return
        }

        historyRequestId.current += 1
    }, [isOpen, lineItem?.id, loadHistory])

    if (!lineItem) return null

    const addedQuantity = desiredQuantity - currentQuantity

    const handleSave = async () => {
        if (addedQuantity <= 0 || isSaving) return

        setIsSaving(true)
        const notificationId = startNotif('Updating line item quantity...')

        try {
            await increaseQuantity(orderId, lineItem.id, desiredQuantity)
            setCurrentQuantity(desiredQuantity)
            onQuantityUpdated(lineItem.id, desiredQuantity)
            closeNotif(
                notificationId,
                `Line item quantity increased by ${addedQuantity}.`,
            )
            onClose()
        } catch (error) {
            closeNotif(
                notificationId,
                getErrorMessage(error, 'Unable to increase the line item quantity.'),
                {type: 'error'},
            )
        } finally {
            setIsSaving(false)
        }
    }

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title="Quantity & History"
            className="!w-[640px] max-w-[calc(100vw-32px)] max-h-[calc(100vh-300px)] max-md:max-h-[calc(100vh-160px)] overflow-y-auto overscroll-contain !p-0"
            containerClassName="!overflow-hidden max-md:!py-[32px]"
        >
            <div>
                <section className="border-b border-gray-200 p-xl">
                    <div className="flex flex-wrap items-start gap-lg">
                        <div className="min-w-0">
                            <p className="truncate text-title-md font-medium text-gray-900">{lineItem.name}</p>
                            <p className="mt-xs text-xs text-gray-400">SKU #{lineItem.sku}</p>
                        </div>
                    </div>

                    <div className="mt-lg grid grid-cols-3 gap-md">
                        <div className="rounded-md bg-gray-50 p-md">
                            <p className="text-xs text-gray-400">Total Quantity</p>
                            <p className="mt-xs text-title-lg font-medium text-gray-900">{currentQuantity}</p>
                        </div>
                        <div className="rounded-md bg-gray-50 p-md">
                            <p className="text-xs text-gray-400">Processed</p>
                            <p className="mt-xs text-title-lg font-medium text-[#027A48]">{lineItem.quantity_done}</p>
                        </div>
                        <div className="rounded-md bg-gray-50 p-md">
                            <p className="text-xs text-gray-400">Failed Attempts</p>
                            <p className={`mt-xs text-title-lg font-medium ${lineItem.quantity_failed > 0 ? 'text-[#D92D20]' : 'text-gray-900'}`}>
                                {lineItem.quantity_failed}
                            </p>
                        </div>
                    </div>
                </section>

                <section className="border-b border-gray-200 p-xl">
                    <div className="flex flex-wrap items-end justify-between gap-lg">
                        <div>
                            <p className="text-title-md font-medium text-gray-900">Increase Quantity</p>
                            <p className="mt-xs text-xs text-gray-400">
                                Quantity can be increased, but cannot be reduced below {currentQuantity}.
                            </p>
                        </div>

                        <div className="flex items-center gap-sm">
                            <Button
                                type="alt"
                                className="!h-[40px] !w-[40px] !p-0"
                                icon={<Minus size={16}/>} 
                                disabled={desiredQuantity <= currentQuantity || isSaving}
                                onClick={() => setDesiredQuantity(value => Math.max(currentQuantity, value - 1))}
                            >
                                <span className="sr-only">Decrease quantity</span>
                            </Button>
                            <span className="min-w-[56px] text-center text-title-xl font-medium text-gray-900" aria-live="polite">
                                {desiredQuantity}
                            </span>
                            <Button
                                type="alt"
                                className="!h-[40px] !w-[40px] !p-0"
                                icon={<Plus size={16}/>} 
                                disabled={isSaving}
                                onClick={() => setDesiredQuantity(value => value + 1)}
                            >
                                <span className="sr-only">Increase quantity</span>
                            </Button>
                        </div>
                    </div>

                    <Button
                        className="mt-lg w-full"
                        disabled={addedQuantity === 0 || isSaving}
                        onClick={handleSave}
                    >
                        {isSaving
                            ? `Increasing Quantity by ${addedQuantity}...`
                            : `Increase Quantity by ${addedQuantity}`}
                    </Button>
                </section>

                <section className="p-xl">
                    <HistoryTimeline
                        title="Line Item History"
                        history={history}
                        isLoading={isHistoryLoading}
                        hasError={Boolean(historyError)}
                        onRefresh={loadHistory}
                        emptyMessage="No changes have been recorded for this line item yet."
                        errorMessage={historyError}
                    />

                    <OrderJobsPanel jobs={lineItem.jobs}/>
                </section>
            </div>
        </Modal>
    )
}

export default LineItemProgressHistoryDialog
